import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {select, Store} from '@ngrx/store';
import {switchMap, take} from 'rxjs/operators';

import {getIlps, StoreState} from '@redux/reducers';
import {SharedFacadeService} from '@shared/shared-facade.service';
import {
  PedagogicalFollowUpCategoryDeleted,
  PedagogicalFollowUpCategorySaved,
  PedagogicalFollowUpILPDeleted,
  PedagogicalFollowUpILPLoaded,
  PedagogicalFollowUpILPSaved
} from '@redux/actions/pedagogical-follow-up.action';
import {SchoolService} from '@services/school.service';
import {PedagogicalFollowUpCategory} from '@model/pedagogical-follow-up-category';
import {School} from '@model/school';
import {IndividualLearningPlan} from '@model/individual-learning-plan';
import {StudentSearchData} from '@model/student-search-data';
import {StudentView} from '@model/student-view';
import {Page} from '@model/page';
import {StaffMember} from '@model/staffMember';
import {PedagogicalFollowUpComment} from '@model/pedagogical-follow-up-comment';
import {Profile} from '@model/profile';
import {documentGenerationRequestStarted, documentGenerationRequestStopped} from '@redux/actions/ui.action';

@Injectable()
export class PedagogicalFollowUpFacadeService {

  school$: Observable<School>;
  profile$: Observable<Profile>;
  pedagogicalFollowUpCategories$: Observable<PedagogicalFollowUpCategory[]>;
  individualLearningPlans$: Observable<IndividualLearningPlan[]>;
  pedagogicalFollowUpComments$: Observable<PedagogicalFollowUpComment[]>;
  studentSearchResult$: Observable<Page<StudentView>>;
  staffMembers$: Observable<StaffMember[]>;
  hasIlpManagementRole$: Observable<boolean>;
  documentGenerationRequestOngoing$: Observable<boolean>;

  constructor(private _store: Store<StoreState>,
    private _sharedFacade: SharedFacadeService,
    private _schoolService: SchoolService,
    ) {
      this.school$ = this._sharedFacade.school$;
      this.pedagogicalFollowUpCategories$ = this._sharedFacade.pedagogicalFollowUpCategories$;
      this.individualLearningPlans$ = this._store.pipe(select(getIlps));
      this.pedagogicalFollowUpComments$ = this._sharedFacade.pedagogicalFollowUpComments$;
      this.studentSearchResult$ = this._sharedFacade.studentSearchResult$;
      this.staffMembers$ = this._sharedFacade.staffMembers$;
      this.hasIlpManagementRole$ = this._sharedFacade.hasIlpManagementRole$;
      this.profile$ = this._sharedFacade.profile$;
      this.documentGenerationRequestOngoing$ = this._sharedFacade.documentGenerationRequestOngoing$;
    }

  loadPedagogicalFollowUpCategories() {
    this._sharedFacade.loadPedagogicalFollowUpCategories();
  }

  savePedagogicalFollowUpCategory(category: PedagogicalFollowUpCategory) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.savePedagogicalFollowUpCategory(school.id, category))
    ).subscribe(categoryId => this._store.dispatch(PedagogicalFollowUpCategorySaved({ category: {id: categoryId, ...category} })));
  }

  savePositionedPedagogicalFollowUpCategory(category: PedagogicalFollowUpCategory) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.savePositionPedagogicalFollowUpCategory(school.id, category))
    ).subscribe(() => this.loadPedagogicalFollowUpCategories());
  }

  removePedagogicalFollowUpCategory(categoryId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.removePedagogicalFollowUpCategory(school.id, categoryId))
    ).subscribe(() => this._store.dispatch(PedagogicalFollowUpCategoryDeleted({ categoryId })));
  }

  loadIndividualLearningPlans(includeArchived: boolean = true) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.getIndividualLearningPlans(school.id, includeArchived))
    ).subscribe((ilp) =>
      this._store.dispatch(PedagogicalFollowUpILPLoaded({ ilp: ilp })));
  }

  saveIndividualLearningPlan(ilp: IndividualLearningPlan) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.saveIndividualLearningPlan(school.id, ilp.studentId, ilp.referentId, ilp.creationDate))
    ).subscribe(() =>
      this._store.dispatch(PedagogicalFollowUpILPSaved({ ilp: ilp })));
  }

  deleteIndividualLearningPlan(studentId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.deleteIndividualLearningPlan(school.id, studentId))
    ).subscribe(() =>
      this._store.dispatch(PedagogicalFollowUpILPDeleted({ studentId }))
    );
  }

  archiveIndividualLearningPlan(studentId: number, archived: boolean) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.patchArchiveIndividualLearningPlan(school.id, studentId, archived))
    ).subscribe(() =>
      this._store.dispatch(PedagogicalFollowUpILPSaved({ ilp: { studentId,  archived} }))
    );
  }

  searchStudents(searchData: StudentSearchData): void {
    this._sharedFacade.searchStudents(searchData);
  }

  resetStudentSearchResult(): void {
    this._sharedFacade.resetStudentSearchResult();
  }

  loadStaffMembers() {
    this._sharedFacade.loadStaffMembers();
  }

  savePedagogicalFollowUpComment(ilpComment: PedagogicalFollowUpComment) {
    this._sharedFacade.savePedagogicalFollowUpComment(ilpComment);
  }

  loadPedagogicalFollowUpComments(studentId: number) {
    this._sharedFacade.loadPedagogicalFollowUpComments(studentId);
  }

  deletePedagogicalFollowUpComment(studentId: number, commentId: number) {
    this._sharedFacade.deletePedagogicalFollowUpComment(studentId, commentId);
  }

  resetPedagogicalFollowUpComments() {
    this._sharedFacade.resetPedagogicalFollowUpComments();
  }

  downloadIndividualLearningPlan(studentId: number, confidentialityLevel: string) {
    this._store.dispatch(documentGenerationRequestStarted());
    this.school$.pipe(
      take(1),
      switchMap(school =>
        this._schoolService.createIndividualLearningPlanByCategoryGenerationRequest(school.id, studentId, confidentialityLevel))
    ).subscribe({
      next: (locationUrl) => this._sharedFacade.startPollingDocument(locationUrl, documentGenerationRequestStopped()),
      error: (err) => {
        this._store.dispatch(documentGenerationRequestStopped());
        throw err;
      }
    });
  }
}
